import React, { useRef, useState, useEffect, useId, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, AnimatePresence } from "framer-motion";
import "../styles/homepage.css";

// Reusable fade-up variant
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40,
  },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.2,
      delay,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

// Hook that returns whether a ref is in view.
function useScrollReveal() {
  const ref = useRef(null);

  const inView = useInView(ref, {
    once: true,
    amount: 0.25,
  });

  return { ref, inView };
}

// ── BREATHING CIRCLE ──
const phases = [
  { label: "inhale", text: "Breathe in.", duration: 4000 },
  { label: "hold", text: "Stay here a moment.", duration: 4000 },
  { label: "exhale", text: "Let it go.", duration: 6000 },
  { label: "rest", text: "Rest.", duration: 3000 },
];

function BreathingCircle() {
  const totalMs = phases.reduce((sum, p) => sum + p.duration, 0);

  const [isActive, setIsActive] = useState(false);
  const [phaseIndex, setPhaseIndex] = useState(0);
  const [cycleKey, setCycleKey] = useState(0);

  const circleRef = useRef(null);
  const glowRef = useRef(null);
  const settleTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;
    const timer = setTimeout(() => {
      setPhaseIndex((i) => (i + 1) % phases.length);
    }, phases[phaseIndex].duration);
    return () => clearTimeout(timer);
  }, [phaseIndex, isActive]);

  useEffect(() => {
    return () => {
      if (settleTimeoutRef.current) clearTimeout(settleTimeoutRef.current);
    };
  }, []);

  const getComputedScale = (el) => {
    if (!el) return 1;
    const { transform } = window.getComputedStyle(el);
    if (!transform || transform === "none") return 1;
    const match = transform.match(/matrix\(([^)]+)\)/);
    if (!match) return 1;
    const [a] = match[1].split(",").map(Number);
    return Number.isFinite(a) ? a : 1;
  };

  const settle_ms = 1300;

  const handleToggle = () => {
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }

    if (!isActive) {
      [circleRef.current, glowRef.current].forEach((el) => {
        if (!el) return;
        el.style.transition = "none";
        el.style.transform = "";
      });
      setPhaseIndex(0);
      setCycleKey((k) => k + 1);
      setIsActive(true);
      return;
    }

    const scale = getComputedScale(circleRef.current);
    [circleRef.current, glowRef.current].forEach((el) => {
      if (!el) return;
      el.style.transition = "none";
      el.style.transform = `scale(${scale})`;
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        [circleRef.current, glowRef.current].forEach((el) => {
          if (!el) return;
          el.style.transition = `transform ${settle_ms}ms cubic-bezier(0.22, 1, 0.36, 1)`;
          el.style.transform = "scale(1)";
        });
      });
    });

    settleTimeoutRef.current = setTimeout(() => {
      [circleRef.current, glowRef.current].forEach((el) => {
        if (!el) return;
        el.style.transition = "";
      });
    }, settle_ms);

    setIsActive(false);
  };

  const idleText =
    phaseIndex === 0 && !isActive ? "Ready when you are." : "Paused.";

  return (
    <div className="breathing-circle-wrap-outer">
      <div
        className="breathing-circle-visual"
        style={{ "--breathe-duration": `${totalMs / 1000}s` }}
      >
        <div
          key={`glow-${cycleKey}`}
          ref={glowRef}
          className={`breathing-circle-glow${isActive ? " is-breathing" : ""}`}
        />
        <div
          key={`circle-${cycleKey}`}
          ref={circleRef}
          className={`breathing-circle${isActive ? " is-breathing" : ""}`}
        />
      </div>

      <div className="breathing-phase-text-wrap">
        {!isActive ? (
          <p className="breathing-phase-text">{idleText}</p>
        ) : (
          <AnimatePresence mode="wait">
            <motion.p
              key={phaseIndex}
              className="breathing-phase-text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
            >
              {phases[phaseIndex].text}
            </motion.p>
          </AnimatePresence>
        )}
      </div>

      <button
        type="button"
        className="breathing-toggle"
        onClick={handleToggle}
        aria-pressed={isActive}
      >
        {isActive ? "Pause" : "Begin breathing"}
      </button>
    </div>
  );
}

// ── GRIP CIRCLE ──
function GripCircle() {
  const gradientId = useId();

  const [stage, setStage] = useState("idle");
  const [label, setLabel] = useState("Press and hold it");

  const svgRef = useRef(null);
  const gRef = useRef(null);
  const holdingRef = useRef(false);
  const rafRef = useRef(null);
  const phaseRef = useRef(0);
  const labelTimerRef = useRef(null);
  const releaseRef = useRef(null);

  const IDLE_MID = 1.0;
  const IDLE_AMP = 0.075;
  const IDLE_PERIOD = 11000;

  const OPEN_DURATION = 6000;

  function openingScale(t) {
    const decay = Math.exp(-3.5 * t);
    const wave = Math.cos(Math.PI * t * 0.9);
    const envelope = decay * wave;
    return 1.0 - 0.2 * envelope;
  }

  const startIdleLoop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    let last = null;
    const tick = (now) => {
      if (last === null) last = now;
      const dt = now - last;
      last = now;
      phaseRef.current += (2 * Math.PI * dt) / IDLE_PERIOD;
      const s = IDLE_MID + IDLE_AMP * Math.sin(phaseRef.current);
      if (gRef.current) gRef.current.style.transform = `scale(${s})`;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopIdleLoop = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  useEffect(() => {
    startIdleLoop();
    return () => stopIdleLoop();
  }, [startIdleLoop, stopIdleLoop]);

  releaseRef.current = () => {
    if (!holdingRef.current) return;
    holdingRef.current = false;
    stopIdleLoop();
    setStage("opening");
    setLabel("It opens");

    const startTime = performance.now();
    const openTick = (now) => {
      const t = Math.min((now - startTime) / OPEN_DURATION, 1);
      const s = openingScale(t);
      if (gRef.current) gRef.current.style.transform = `scale(${s})`;

      if (t < 1) {
        rafRef.current = requestAnimationFrame(openTick);
      } else {
        phaseRef.current = 0;
        setStage("idle");
        startIdleLoop();
      }
    };
    rafRef.current = requestAnimationFrame(openTick);

    if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    labelTimerRef.current = setTimeout(
      () => setLabel("Press and hold it"),
      4000,
    );
  };

  useEffect(() => {
    const handler = () => releaseRef.current();
    window.addEventListener("pointerup", handler);
    window.addEventListener("pointercancel", handler);
    return () => {
      window.removeEventListener("pointerup", handler);
      window.removeEventListener("pointercancel", handler);
      if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    };
  }, []);

  const grip = () => {
    if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    stopIdleLoop();
    holdingRef.current = true;
    setStage("gripping");
    setLabel("It tightens");
  };

  const handlePointerDown = (e) => {
    if (e.cancelable) e.preventDefault();
    grip();
  };
  const handleKeyDown = (e) => {
    if ((e.key === " " || e.key === "Enter") && !holdingRef.current) {
      e.preventDefault();
      grip();
    }
  };
  const handleKeyUp = (e) => {
    if (e.key === " " || e.key === "Enter") releaseRef.current();
  };

  const isGripping = stage === "gripping";

  return (
    <div className="grip-wrap">
      <div className="grip-visual">
        <svg
          ref={svgRef}
          className={["grip-stage", isGripping ? "gripping" : ""]
            .filter(Boolean)
            .join(" ")}
          viewBox="0 0 400 280"
          role="img"
          tabIndex={0}
          aria-label="Press and hold to feel the grip tighten. Release to let it open again."
          onPointerDown={handlePointerDown}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
        >
          <defs>
            <radialGradient id={gradientId} cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#8B7355" stopOpacity="0.55" />
              <stop offset="55%" stopColor="#8B7355" stopOpacity="0.18" />
              <stop offset="100%" stopColor="#8B7355" stopOpacity="0" />
            </radialGradient>
          </defs>
          <g ref={gRef} className="grip-breath">
            <path
              className="grip-mandorla"
              fill={`url(#${gradientId})`}
              d="M 200 70.72 A 80 80 0 0 1 200 209.28 A 80 80 0 0 1 200 70.72 Z"
            />
            <circle
              className="grip-ring grip-ring-a"
              cx="160"
              cy="140"
              r="80"
            />
            <circle
              className="grip-ring grip-ring-b"
              cx="240"
              cy="140"
              r="80"
            />
            <circle className="grip-seed" cx="200" cy="140" r="2.4" />
          </g>
        </svg>
      </div>
      <AnimatePresence mode="wait">
        <motion.p
          key={label}
          className="grip-label"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          {label}
        </motion.p>
      </AnimatePresence>
    </div>
  );
}

export default function HomePage() {
  const { ref: finalCtaRef, inView: finalCtaInView } = useScrollReveal();

  return (
    <>
      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-glow hero-glow-a" aria-hidden="true" />
        <div className="hero-glow hero-glow-b" aria-hidden="true" />

        <div className="hero-content">
          <p className="section-label">For the thing you can't quite name yet</p>
          <h1 className="hero-headline">
            Some part of you
            <br />
            <em>already knows.</em>
          </h1>
          <p className="hero-sub">
            You've felt it — beneath the momentum, the responsibility, the
            achievements that were supposed to be enough. Not always a crisis.
            Sometimes just the quiet signal that the way you've been living,
            leading, or striving is no longer the whole story.
          </p>
          <div className="hero-recognition">
            <p>
              If you've felt a quiet sense that something is off — in how you
              lead, how you work, or how you live — and you can't quite name it
              yet:
            </p>
            <strong>you're not imagining it.</strong>
          </div>
          <div className="hero-actions">
            <div className="hero-actions-row">
              <Link to="/assessment" className="btn-primary">
                Begin noticing where you are →
              </Link>
            </div>
            <p className="hero-note">
              Free • Takes 10 minutes • Receive your results immediately
            </p>
          </div>
        </div>
      </section>

      {/* ── RECOGNITION ── */}
      <motion.section
        className="recognition"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={fadeUp}
      >
        <div className="recognition-inner">
          <div className="recognition-grid-layout">
            <div className="recognition-intro">
              <p className="section-label">Is this for you?</p>

              <h2 className="recognition-heading">
                Maybe something's already <em>asking for your attention.</em>
              </h2>

              {/* <p className="recognition-body">
                Some people arrive here because achievement stopped answering
                the questions they were asking. Others because success somehow
                began feeling smaller than expected. Others because they can
                sense something alive underneath everything they've built.
              </p> */}
            </div>

            <div className="readiness-card">
              <p className="readiness-card-label">This might be for you if:</p>

              <div className="readiness-points">
                <div className="readiness-point">
                  <span className="readiness-mark" aria-hidden="true">
                    ✓
                  </span>
                  <p>
                    You've achieved things that once mattered deeply — and
                    they're{" "}
                    <strong>
                      no longer answering the questions you're actually living
                      with.
                    </strong>
                  </p>
                </div>

                <div className="readiness-point">
                  <span className="readiness-mark" aria-hidden="true">
                    ✓
                  </span>
                  <p>
                    You feel capable on the outside, but{" "}
                    <strong>
                      inwardly unsettled, overextended, or quietly out of step
                      with yourself.
                    </strong>
                  </p>
                </div>

                <div className="readiness-point">
                  <span className="readiness-mark" aria-hidden="true">
                    ✓
                  </span>
                  <p>
                    You're surrounded by capable people — and{" "}
                    <strong>
                      real, honest conversation, the kind where no one's
                      performing,
                    </strong>{" "}
                    has grown rare.
                  </p>
                </div>

                <div className="readiness-point">
                  <span className="readiness-mark" aria-hidden="true">
                    ✓
                  </span>
                  <p>
                    You've been reaching for the next tool, the better strategy,
                    more control — and some part of you senses{" "}
                    <strong>
                      the real shift needs to happen somewhere deeper.
                    </strong>
                  </p>
                </div>
              </div>

              <p className="readiness-context">
                Sometimes this begins in burnout, a transition, grief, or
                frustration — sometimes just the strange realization that a life
                that looks fine from the outside no longer feels whole on the
                inside.
              </p>

              <p className="readiness-note-inline">
                Readiness is not certainty. It is willingness.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* ── TRUTH BAND ── */}
      {/* Non-verbal divider: keeps the visual pause between sections without
          restating "already / present all along" (echoed in the hero and in
          the Expand caption below). Mark reuses the site's vesica-piscis /
          mandorla motif from the Grip exercise for visual continuity. */}
      <div className="truth">
        <motion.svg
          className="truth-mark"
          viewBox="0 0 80 40"
          role="img"
          aria-hidden="true"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 1.4 }}
        >
          <circle
            className="truth-mark-ring truth-mark-ring-a"
            cx="32"
            cy="20"
            r="16"
          />
          <circle
            className="truth-mark-ring truth-mark-ring-b"
            cx="48"
            cy="20"
            r="16"
          />
        </motion.svg>
      </div>

      {/* ── BREATHING CIRCLE + GRIP EXERCISE / EMBODIED PAUSE ── */}
      <motion.section
        className="breathing-tool"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="breathing-inner">
          <p className="section-label">A moment to pause</p>
          <h2 className="section-title">Notice how you meet a feeling.</h2>
          <p className="breathing-intro">
            So much of life asks us to keep moving — toward the next task, the
            next goal, the next version of ourselves. For a few breaths, notice
            two different ways of responding. 
          </p>

          <div className="practices-duo-grid">
            <div className="practice-col">
              <h3 className="practice-heading">Grip</h3>
              <p className="practice-desc">
                Press and hold. Feel how quickly the light tightens and dims —
                then let go, and watch it open again on its own.
              </p>
              <GripCircle />
            </div>

            <div className="practice-col">
              <h3 className="practice-heading">Expand</h3>
              <p className="practice-desc">
                Follow the circle as it slowly widens and settles on its own
                rhythm. Let your own breath match its pace.
              </p>
              <BreathingCircle />
            </div>
          </div>

          <p className="breathing-closing">
            Sometimes what we discover in the pause is not something new, but
            something that was quietly present all along.
          </p>
        </div>
      </motion.section>

      {/* ── FINAL CTA ── */}
      <section ref={finalCtaRef} className="final-cta">
        <motion.div
          className="final-cta-content"
          initial={{ opacity: 0 }}
          animate={finalCtaInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{
            duration: 1.6,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <h2>
            You've recognized something.
            <br />
            Don't rush past it.
          </h2>

          <p>
            If pieces of this page felt strangely familiar, the Flow Identity
            Assessment is a simple place to explore what's stirring — and to see
            whether this work is the right next step for you.
          </p>

          <div className="cta-group">
            <Link to="/assessment" className="btn-light">
              Begin with the Flow Identity Assessment →
            </Link>
          </div>
        </motion.div>
      </section>
    </>
  );
}
